// fileValidationUtils.js

/**
 * Allowed file extensions and their corresponding MIME types
 */
const ALLOWED_FILE_TYPES = {
  // PDF files
  'pdf': ['application/pdf'],
  
  // Document files
  'doc': ['application/msword'],
  'docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  
  // Text files
  'txt': ['text/plain'],
  
  // Image files
  'png': ['image/png'],
  'jpeg': ['image/jpeg'],
  'jpg': ['image/jpeg']
};

/**
 * Get all allowed extensions as an array
 */
const getAllowedExtensions = () => {
  return Object.keys(ALLOWED_FILE_TYPES);
};

/**
 * Get all allowed MIME types as an array
 */
const getAllowedMimeTypes = () => {
  return Object.values(ALLOWED_FILE_TYPES).flat();
};

/**
 * Extract file extension from filename
 * @param {string} filename - The filename to extract extension from
 * @returns {string} - The lowercase file extension without the dot
 */
const getFileExtension = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return '';
  }
  
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return '';
  }
  
  return filename.slice(lastDotIndex + 1).toLowerCase();
};

/**
 * Validate file type based on extension and MIME type
 * @param {File} file - The file object to validate
 * @returns {Object} - Validation result object
 */
export const validateFileType = (file) => {
  // Check if file object is valid
  if (!file) {
    return {
      isValid: false,
      error: 'No file provided',
      errorType: 'NO_FILE'
    };
  }

  // Check if it's actually a File object
  if (!(file instanceof File)) {
    return {
      isValid: false,
      error: 'Invalid file object',
      errorType: 'INVALID_FILE_OBJECT'
    };
  }

  const filename = file.name;
  const mimeType = file.type;
  const fileSize = file.size;
  
  // Check file size (optional - you can set a max size limit)
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  if (fileSize > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: `File size too large. Maximum allowed size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
      errorType: 'FILE_TOO_LARGE'
    };
  }

  // Extract file extension
  const extension = getFileExtension(filename);
  
  if (!extension) {
    return {
      isValid: false,
      error: 'File must have a valid extension',
      errorType: 'NO_EXTENSION'
    };
  }

  // Check if extension is allowed
  if (!ALLOWED_FILE_TYPES[extension]) {
    const allowedExtensions = getAllowedExtensions();
    return {
      isValid: false,
      error: `File type not supported. Allowed formats: ${allowedExtensions.join(', ').toUpperCase()}`,
      errorType: 'INVALID_EXTENSION'
    };
  }

  // Check MIME type if available
  if (mimeType) {
    const allowedMimeTypes = ALLOWED_FILE_TYPES[extension];
    if (!allowedMimeTypes.includes(mimeType)) {
      // Some browsers might not set the correct MIME type, so we'll give a warning but not fail
      console.warn(`MIME type mismatch: expected ${allowedMimeTypes.join(' or ')}, got ${mimeType}`);
    }
  }

  // File is valid
  return {
    isValid: true,
    extension: extension,
    mimeType: mimeType,
    size: fileSize,
    message: 'File type is valid'
  };
};

/**
 * Validate multiple files at once
 * @param {FileList|Array} files - Files to validate
 * @returns {Object} - Validation results for all files
 */
export const validateMultipleFiles = (files) => {
  if (!files || files.length === 0) {
    return {
      isValid: false,
      error: 'No files provided',
      results: []
    };
  }

  const results = [];
  let allValid = true;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const validation = validateFileType(file);
    
    results.push({
      file: file,
      filename: file.name,
      ...validation
    });

    if (!validation.isValid) {
      allValid = false;
    }
  }

  return {
    isValid: allValid,
    results: results,
    validCount: results.filter(r => r.isValid).length,
    invalidCount: results.filter(r => !r.isValid).length
  };
};

/**
 * Get user-friendly error message
 * @param {string} errorType - Error type from validation result
 * @param {string} customMessage - Custom error message
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (errorType, customMessage = '') => {
  const errorMessages = {
    'NO_FILE': 'Please select a file to upload',
    'INVALID_FILE_OBJECT': 'Invalid file selected',
    'FILE_TOO_LARGE': customMessage || 'File size is too large',
    'NO_EXTENSION': 'Please select a file with a valid extension',
    'INVALID_EXTENSION': customMessage || `Only ${getAllowedExtensions().join(', ').toUpperCase()} files are allowed`
  };

  return errorMessages[errorType] || customMessage || 'Invalid file type';
};

/**
 * Utility function to check if a filename has allowed extension
 * @param {string} filename - Filename to check
 * @returns {boolean} - True if extension is allowed
 */
export const hasAllowedExtension = (filename) => {
  const extension = getFileExtension(filename);
  return ALLOWED_FILE_TYPES.hasOwnProperty(extension);
};

/**
 * Get allowed file extensions as a string for input accept attribute
 * @returns {string} - Comma-separated list of extensions for HTML input accept
 */
export const getAcceptString = () => {
  const extensions = getAllowedExtensions().map(ext => `.${ext}`);
  const mimeTypes = getAllowedMimeTypes();
  
  return [...extensions, ...mimeTypes].join(',');
};

// Export constants for use in other files
export { ALLOWED_FILE_TYPES, getAllowedExtensions, getAllowedMimeTypes };